import React, { useEffect, useRef } from 'react';

interface TimePickerProps {
    value: string; // HH:mm (24h format)
    onChange: (value: string) => void;
    isDark?: boolean;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

const ITEM_HEIGHT = 40; // Height of each item in pixels

const Wheel: React.FC<{
    items: (string | number)[];
    selectedValue: string | number;
    onChange: (value: string | number) => void;
    isDark?: boolean;
}> = ({ items, selectedValue, onChange, isDark }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isScrolling = useRef(false);

    // Scroll to selected value on mount and update
    useEffect(() => {
        if (containerRef.current && !isScrolling.current) {
            const index = items.indexOf(selectedValue);
            if (index !== -1) {
                containerRef.current.scrollTop = index * ITEM_HEIGHT;
            }
        }
    }, [selectedValue, items]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        isScrolling.current = true;
        const scrollTop = e.currentTarget.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);

        // Debounce the selection update to avoid jitter
        if (items[index] !== undefined && items[index] !== selectedValue) {
            // We don't update immediately to avoid fighting with the scroll
            // Instead we wait for scroll end or use a timeout
            // For simplicity in this implementation, we'll just set it
            // But in a real app, we might want to wait for scroll end
        }

        // Reset scrolling flag after a delay
        setTimeout(() => {
            isScrolling.current = false;
        }, 150);
    };

    // Use onScrollCapture to detect the end of scroll more reliably if needed, 
    // but for a simple wheel, checking scroll position on scroll end is better.
    // Here we use a simplified approach: update on scroll end (snap)
    const handleScrollEnd = (e: React.UIEvent<HTMLDivElement>) => {
        const scrollTop = e.currentTarget.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        if (items[index] !== undefined) {
            onChange(items[index]);
        }
    };

    return (
        <div className="relative h-[150px] w-full overflow-hidden flex-1">
            {/* Scroll Container */}
            <div
                ref={containerRef}
                className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide py-[55px]"
                onScroll={handleScroll}
                onMouseUp={handleScrollEnd}
                onTouchEnd={handleScrollEnd}
                style={{
                    scrollBehavior: 'smooth',
                    // Hide scrollbar for Firefox/IE/Edge
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                {items.map((item) => (
                    <div
                        key={item}
                        className={`h-[40px] flex items-center justify-center snap-center text-lg font-medium transition-all duration-200 ${item === selectedValue
                            ? (isDark ? 'text-white scale-110' : 'text-gray-900 scale-110')
                            : (isDark ? 'text-white/40 scale-95' : 'text-gray-400 scale-95')
                            }`}
                    >
                        {item}
                    </div>
                ))}
            </div>
        </div>
    );
};

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, isDark = true }) => {
    // Parse 24h time to 12h format
    const parseTime = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const hour12 = h % 12 || 12;
        const minute = m.toString().padStart(2, '0');
        return { hour: hour12, minute, period };
    };

    const { hour, minute, period } = parseTime(value);

    const updateTime = (newHour: number, newMinute: string, newPeriod: string) => {
        let h = newHour;
        if (newPeriod === 'PM' && h !== 12) h += 12;
        if (newPeriod === 'AM' && h === 12) h = 0;

        const timeStr = `${h.toString().padStart(2, '0')}:${newMinute}`;
        onChange(timeStr);
    };

    return (
        <div className="relative w-full max-w-[280px] mx-auto h-[150px]">
            {/* Unified Highlight Bar */}
            <div
                className={`absolute top-[55px] left-0 right-0 h-[40px] pointer-events-none z-0 rounded-xl ${isDark ? 'bg-white/10' : 'bg-gray-100'
                    }`}
            />

            {/* Wheels Container with Gradient Mask for "Round Surface" Effect */}
            <div
                className="flex justify-center items-center h-full relative z-10 gap-1"
                style={{
                    maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)'
                }}
            >
                {/* Hour Wheel */}
                <div className="w-14">
                    <Wheel
                        items={HOURS}
                        selectedValue={hour}
                        onChange={(val) => updateTime(Number(val), minute, period)}
                        isDark={isDark}
                    />
                </div>

                {/* Minute Wheel */}
                <div className="w-14">
                    <Wheel
                        items={MINUTES}
                        selectedValue={minute}
                        onChange={(val) => updateTime(hour, String(val), period)}
                        isDark={isDark}
                    />
                </div>

                {/* Period Wheel */}
                <div className="w-14">
                    <Wheel
                        items={PERIODS}
                        selectedValue={period}
                        onChange={(val) => updateTime(hour, minute, String(val))}
                        isDark={isDark}
                    />
                </div>
            </div>
        </div>
    );
};
